import { useState, useEffect } from "react";
import api from "@/lib/api";
import { DemoBanner } from "@/components/DemoBanner";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useAuth } from "@/context/AuthContext";
import { useEstablishments } from "@/context/EstablishmentContext";
import { Logo } from "@/components/Logo";
import { Wrench, GraduationCap,
  LayoutDashboard, ListChecks, Star, Globe, Wand2, Share2, Swords, Building2,
  Settings, Shield, LogOut, Menu, X, ChevronDown, Plus, Sparkles, Bot, Activity, Receipt,
  Heart, CalendarDays, Users, ShoppingCart, ClipboardCheck, Bell, Zap, CreditCard, MessageSquare, Send,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { LiaPanel } from "@/components/lia/LiaPanel";
import { NotificationBell } from "@/components/NotificationCenter";
import { MessagesButton } from "@/components/messaging/MessagesButton";

const NAV = [
  { group: "Pilotage", to: "/app/dashboard", label: "Tableau de bord", icon: LayoutDashboard, id: "dashboard" },
  { group: "Pilotage", to: "/app/messages", label: "Messages", icon: MessageSquare, id: "messages" },
  { group: "Pilotage", to: "/app/notifications", label: "Notifications", icon: Bell, id: "notifications", exact: true },
  { group: "Pilotage", to: "/app/activity", label: "Statistiques & Activité", icon: Activity, id: "activity" },
  { group: "Pilotage", to: "/app/plan", label: "Plan d'action", icon: ListChecks, id: "plan" },
  { group: "Pilotage", to: "/app/advisor", label: "Lia — Conseiller IA", icon: Sparkles, id: "advisor" },
  { group: "Gestion", to: "/app/purchases", label: "Achats & Marges", icon: Receipt, id: "purchases" },
  { group: "Gestion", to: "/app/commandes", label: "Commandes", icon: ShoppingCart, id: "orders" },
  { group: "Gestion", to: "/app/controles", label: "Contrôles & HACCP", icon: ClipboardCheck, id: "controls" },
  { group: "Gestion", to: "/app/rh", label: "Personnel & RH", icon: Users, id: "hr" },
  { group: "Gestion", to: "/app/agenda", label: "Agenda", icon: CalendarDays, id: "agenda" },
  { group: "Clients & Marketing", to: "/app/fidelite", label: "Clients & fidélité", icon: Heart, id: "loyalty" },
  { group: "Clients & Marketing", to: "/app/google", label: "Google & Avis", icon: Star, id: "google" },
  { group: "Clients & Marketing", to: "/app/site-audit", label: "Audit du site", icon: Globe, id: "site-audit" },
  { group: "Clients & Marketing", to: "/app/social", label: "Réseaux sociaux", icon: Share2, id: "social" },
  { group: "Clients & Marketing", to: "/app/competitors", label: "Concurrents", icon: Swords, id: "competitors" },
  { group: "Paramètres", to: "/app/establishments", label: "Établissements", icon: Building2, id: "establishments" },
  { group: "Paramètres", to: "/app/rh?tab=hr", label: "Utilisateurs et permissions", icon: Shield, id: "users-permissions", exact: true },
  { group: "Paramètres", to: "/app/settings", label: "Paramètres généraux", icon: Settings, id: "settings" },
  { group: "Paramètres", to: "/app/notifications/preferences", label: "Préférences de notifications", icon: Settings, id: "notification-prefs" },
  { group: "Paramètres", to: "/app/notifications/diffusion", label: "Diffusion des rapports & alertes", icon: Send, id: "distribution" },
  { group: "Paramètres", to: "/app/admin?tab=automations", label: "Automatisations", icon: Zap, id: "automations", admin: true, exact: true },
  { group: "Paramètres", to: "/app/admin?tab=plans", label: "Facturation et abonnement", icon: CreditCard, id: "billing", admin: true, exact: true },
  { group: "Paramètres", to: "/app/admin", label: "Autres réglages", icon: Wrench, id: "admin", admin: true, exact: true },
];
const GROUPS = ["Pilotage", "Gestion", "Clients & Marketing", "Paramètres"];

export function AppLayout() {
  const { user, logout } = useAuth();
  const { establishments, current, setCurrent } = useEstablishments();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [features, setFeatures] = useState({ reputation_reviews_enabled: false, ai_visibility_enabled: true });
  useEffect(() => { api.get("/features").then((r) => setFeatures(r.data)).catch(() => {}); }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-slate-100">
        <Logo />
      </div>
      <div className="px-4 py-4 border-b border-slate-100">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5 block">Établissement</label>
        <Select value={current?.id || ""} onValueChange={(v) => { setCurrent(v); setMobileOpen(false); }}>
          <SelectTrigger data-testid="establishment-switcher" className="bg-slate-50 border-slate-200">
            <SelectValue placeholder="Choisir" />
          </SelectTrigger>
          <SelectContent>
            {establishments.map((e) => (
              <SelectItem key={e.id} value={e.id} data-testid={`est-option-${e.id}`}>
                {e.name} {e.is_demo ? "· DÉMO" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <nav className="flex-1 px-3 py-3 overflow-y-auto">
        {GROUPS.map((g) => (
          <div key={g} className="mb-3">
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{g}</p>
            <div className="space-y-0.5">
            {NAV.filter((item) => item.group === g && (!item.flag || features[item.flag]) && (!item.admin || user?.role === "admin")).map((item) => (
          <NavLink
            key={item.id} to={item.to} onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.id}`}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200",
              (item.exact ? `${pathname}${search}` === item.to : isActive && !NAV.some((o) => o.exact && o !== item && `${pathname}${search}` === o.to)) ? "bg-blue-50 text-blue-700 shadow-sm" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            )}
          >
            <item.icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-100">
        <NavLink to="/app/establishments/new" onClick={() => setMobileOpen(false)} data-testid="nav-add-establishment"
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-colors">
          <Plus className="w-[18px] h-[18px]" /> Ajouter un établissement
        </NavLink>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 bg-white border-r border-slate-200 flex-col fixed inset-y-0 z-30">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 bg-white h-full shadow-xl animate-fadeup"><SidebarInner /></aside>
        </div>
      )}

      <div className="flex-1 lg:pl-72 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/85 border-b border-slate-200 px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between" style={{ borderTop: `3px solid ${current?.brand?.color || "#2563eb"}` }} data-testid="app-header">
          <div className="flex items-center gap-2 min-w-0">
            <button className="lg:hidden h-11 w-11 -ml-2 grid place-items-center rounded-xl text-slate-600 active:bg-slate-100 shrink-0" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            {current && <div className="flex items-center gap-2 min-w-0" data-testid="header-establishment">{current.brand?.logo_url ? <img src={current.brand.logo_url} alt="" className="h-8 max-w-[96px] object-contain shrink-0" /> : <span className="h-8 w-8 rounded-lg grid place-items-center text-white text-sm font-bold shrink-0" style={{ background: current.brand?.color || "#2563eb" }}>{(current.brand?.name || current.name || "?")[0].toUpperCase()}</span>}<span className="text-sm font-semibold text-slate-900 truncate max-w-[140px] sm:max-w-[240px]" data-testid="header-establishment-name">{current.brand?.name || current.name}</span></div>}
            <div className="hidden xl:flex items-center gap-2 text-sm text-slate-500 pl-3 border-l border-slate-200">
              <Sparkles className="w-4 h-4 text-blue-500" />
              <span>Votre plateforme de pilotage intelligente</span>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
          {current && <MessagesButton http={api} base={`/staffing/${current.id}/msg`} linkBase="/app/messages" open={(l) => navigate(l)} />}
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-2.5 min-h-[44px] rounded-xl hover:bg-slate-100 transition-colors">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-500 grid place-items-center text-white text-sm font-semibold">
                  {(user?.name || "?")[0].toUpperCase()}
                </div>
                <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[140px] truncate">{user?.name}</span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-1.5 text-xs text-slate-500 truncate">{user?.email}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="menu-settings">
                <Settings className="w-4 h-4 mr-2" /> Paramètres
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 min-w-0 pb-24 sm:pb-0">
          <DemoBanner />
          <div className="px-4 sm:px-6 lg:px-8 pt-4"><SubscriptionBanner /></div>
          <ErrorBoundary resetKey={pathname}><Outlet /></ErrorBoundary>
        </main>
      </div>
      <LiaPanel />
    </div>
  );
}
